import { useState, useEffect } from "react";
import { vinService } from "./lib/vinService";
import { useNazStore } from "./store/nazStore";
import { confidenceEngine } from "./logic/confidenceEngine";
import { useVIGStore, VIGState } from "./stores/vigStore";
import { useAuthStore } from "./stores/authStore";
import { useVoice } from "./hooks/useVoice";
import { VIGConfirmationModal } from "./components/VIGConfirmationModal";
import { PartsIntelligenceCard } from "./components/features/PartsIntelligenceCard";
import { DiagramViewer } from "./components/features/DiagramViewer";
import { ChecklistEngine } from "./lib/voice/ChecklistEngine";
import { RarePartsRequestModal } from "./components/features/RarePartsRequestModal";
import { RequestsStatusBoard } from "./components/features/RequestsStatusBoard";
import { AdminDashboard } from "./components/features/AdminDashboard";

// Fallback to production worker if env var is missing
const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || "";
const API_URL = `${API_BASE_URL}/diagnose`;

interface DiagnosticAttachment {
    id: string;
    type: 'image' | 'pdf' | 'audio' | 'video';
    data: string;
    name: string;
}

function App() {
    const { diagnosis, updateFromAiResponse, clearSession } = useNazStore();
    const { session, initialize: initVIG, requireConfirmed: _requireConfirmed, setSession } = useVIGStore();
    const { deviceUuid, session: authSession, initialize: initAuth } = useAuthStore();
    const { isRecording, transcript, startListening, stopListening } = useVoice();

    useEffect(() => {
        initAuth();
        initVIG();

        const handleVigRequired = (e: any) => {
            console.log("VIG Required Event:", e.detail);
            alert("⚠️ يرجى تأكيد هوية السيارة أولاً (VIG)");
        };

        window.addEventListener('vig:required', handleVigRequired);
        return () => window.removeEventListener('vig:required', handleVigRequired);
    }, []);

    const [userMessage, setUserMessage] = useState("");
    const [attachments, setAttachments] = useState<DiagnosticAttachment[]>([]);
    const [activeTab, setActiveTab] = useState<'customer' | 'technician' | 'maintenance' | 'admin'>('customer');
    const [gatewayMode, setGatewayMode] = useState<'vin' | 'manual'>('manual');
    const [vin, setVin] = useState("");
    const [vehicleInfo, setVehicleInfo] = useState({
        make: "",
        model: "",
        year: "",
        engine_capacity: "",
        fuel_type: "",
        transmission: "",
        custom_engine: ""
    });

    const [isDbResult, setIsDbResult] = useState(false);
    const [loading, setLoading] = useState(false);
    const [history, setHistory] = useState<any[]>([]);
    const [diagnosisSource, setDiagnosisSource] = useState<string>("");
    const [checklist, setChecklist] = useState<Record<number, boolean>>({});
    const [_isDecodingVin, setIsDecodingVin] = useState(false);
    const [isVerifiedTechnician, setIsVerifiedTechnician] = useState(false);
    const [showTechAuth, setShowTechAuth] = useState(false);
    const [techCodeInput, setTechCodeInput] = useState("");
    const [showSaveModal, setShowSaveModal] = useState(false);
    const [pendingIdentity, _setPendingIdentity] = useState<any>(null); // Kept pendingIdentity as is, used in JSX
    const [showVIGConfirm, setShowVIGConfirm] = useState(false);
    const [parts, setParts] = useState<any[]>([]);
    const [loadingParts, setLoadingParts] = useState(false);
    const [showDiagram, setShowDiagram] = useState(false);
    const [rarePartModalOpen, setRarePartModalOpen] = useState(false);
    const [_selectedRarePart, setSelectedRarePart] = useState<any>(null);
    const [myRareRequests, setMyRareRequests] = useState<any[]>([]);
    const [_loadingRequests, setLoadingRequests] = useState(false); // Restore setLoadingRequests

    useEffect(() => {
        const isAuth = sessionStorage.getItem('naz_tech_auth') === 'true';
        if (isAuth) setIsVerifiedTechnician(true);
    }, []);

    useEffect(() => {
        if (isVerifiedTechnician) {
            sessionStorage.setItem('naz_tech_auth', 'true');
            fetchMyRequests();
        }
    }, [isVerifiedTechnician]);

    // Sync transcript
    useEffect(() => {
        if (transcript) {
            if (activeTab === 'technician') {
                processVoiceCommand(transcript);
            } else {
                setUserMessage(prev => prev + (prev ? " " : "") + transcript);
            }
        }
    }, [transcript]);

    const fetchMyRequests = async () => {
        const sid = authSession?.id || deviceUuid;
        if (!sid) return;
        setLoadingRequests(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/rare-parts/my-requests`, {
                headers: { "X-Session-ID": sid }
            });
            const data = await res.json();
            if (data.status === 'success') setMyRareRequests(data.requests);
        } catch (err) {
            console.error("Failed to fetch requests", err);
        } finally {
            setLoadingRequests(false);
        }
    };

    const handleRarePartSubmit = async (formData: any) => {
        const sid = authSession?.id || deviceUuid;
        if (!sid) {
            alert("يرجى تسجيل الدخول أولاً");
            return;
        }
        try {
            const res = await fetch(`${API_BASE_URL}/api/rare-parts/request`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-Session-ID": sid
                },
                body: JSON.stringify(formData)
            });
            const data = await res.json();
            if (data.status === 'success') {
                alert(data.message);
                if (data.notificationLink) {
                    window.open(data.notificationLink, '_blank');
                }
                fetchMyRequests();
            }
        } catch (err) {
            alert("فشل إرسال الطلب، حاول تاني يا هندسة.");
        }
    };

    const isVehicleReady = !!(gatewayMode === 'vin' ? vin.length > 5 : (
        vehicleInfo.make && vehicleInfo.model && vehicleInfo.year &&
        vehicleInfo.fuel_type && vehicleInfo.transmission &&
        (vehicleInfo.engine_capacity !== 'أخرى' || vehicleInfo.custom_engine)
    ));

    const handleSoftReset = () => {
        clearSession();
        setHistory([]);
        setUserMessage("");
        setAttachments([]);
        setVin("");
        setVehicleInfo({ make: "", model: "", year: "", engine_capacity: "", fuel_type: "", transmission: "", custom_engine: "" });
        setIsDbResult(false);
        setLoading(false);
        setChecklist({});
        setDiagnosisSource("");
    };

    useEffect(() => {
        if (gatewayMode === 'vin' && vin.length === 17) {
            const fetchVinData = async () => {
                setIsDecodingVin(true);
                const data = await vinService.decode(vin);
                if (data) {
                    setVehicleInfo(prev => ({
                        ...prev,
                        make: data.make || prev.make,
                        model: data.model || prev.model,
                        year: data.year || prev.year,
                        engine_capacity: data.engine || prev.engine_capacity,
                        fuel_type: data.fuel || prev.fuel_type,
                        transmission: data.transmission || prev.transmission
                    }));
                }
                setIsDecodingVin(false);
            };
            fetchVinData();
        }
    }, [vin, gatewayMode]);

    const speakSudanese = (text: string) => {
        if (!('speechSynthesis' in window)) return;
        window.speechSynthesis.cancel();
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = 'ar-SA';
        utterance.rate = 0.9;
        window.speechSynthesis.speak(utterance);
    };

    const toggleChecklistItem = (index: number) => {
        const newState = !checklist[index];
        setChecklist(prev => ({ ...prev, [index]: newState }));
        if (newState) speakSudanese(ChecklistEngine.getFeedback('COMPLETE'));
    };

    const processVoiceCommand = (transcript: string) => {
        const command = ChecklistEngine.parseTranscript(transcript);
        if (command.intent === 'COMPLETE') {
            const currentSteps = diagnosis?.technicalGuide?.technical_steps || [];
            const nextIdx = currentSteps.findIndex((_, i) => !checklist[i]);
            if (nextIdx !== -1) toggleChecklistItem(nextIdx);
        } else if (command.intent === 'SKIP') {
            speakSudanese(ChecklistEngine.getFeedback('SKIP'));
        }
    };


    const handleSubmit = async (forceDeepAI = false) => {
        if (!userMessage && attachments.length === 0) return;
        if (!isVehicleReady) return;
        setLoading(true);
        const sid = authSession?.id || deviceUuid;
        try {
            const res = await fetch(API_URL, {
                method: "POST",
                headers: { "Content-Type": "application/json", "X-Session-ID": sid || "" },
                body: JSON.stringify({
                    message: userMessage,
                    attachments: attachments.map(a => ({ type: a.type, data: a.data, name: a.name })),
                    conversation_history: history,
                    vehicle_info: { ...vehicleInfo, vin },
                    skip_db: forceDeepAI,
                    sessionId: sid
                })
            });
            const data = await res.json();
            if (data.status === "success") {
                updateFromAiResponse(data.reply);
                setIsDbResult(data.source === "knowledge_db");
                setDiagnosisSource(data.source === "knowledge_db" ? "OSTA_VERIFIED" : "AI_INFERENCE");
                setHistory(prev => [...prev, { role: "user", content: userMessage }, { role: "assistant", content: JSON.stringify(data.reply) }]);
                fetchParts(data.reply, { ...vehicleInfo, vin });
                setUserMessage("");
                setAttachments([]);
            }
        } catch (err: any) {
            console.error("Diagnosis Error:", err);
            // PROBE: Detailed alert for mobile environment
            alert(`Connection Failed!\nStatus: ${err.name}\nMessage: ${err.message}\nURL: ${API_URL}`);
            setHistory(prev => [...prev, { role: "user", content: userMessage }, { role: "assistant", content: "عذراً، حدث خطأ في الاتصال. راجع التنبيه أعلاه." }]);
        } finally {
            setLoading(false);
        }
    };

    const fetchParts = async (diagnosisData: any, vInfo: any) => {
        const sid = authSession?.id || deviceUuid;
        setLoadingParts(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/parts/match`, {
                method: "POST",
                headers: { "Content-Type": "application/json", "X-Session-ID": sid || "" },
                body: JSON.stringify({ diagnosis: diagnosisData, vehicle_info: vInfo })
            });
            const data = await res.json();
            if (data.status === 'success') setParts(data.parts);
        } catch (err) { } finally { setLoadingParts(false); }
    };

    const handleTechAuthSubmit = () => {
        if (techCodeInput === "NAZ-777") {
            setIsVerifiedTechnician(true);
            setShowTechAuth(false);
            setActiveTab('technician');
        } else {
            alert("الكود خطأ");
        }
    };

    const handleVIGConfirmed = () => {
        setShowVIGConfirm(false);
        if (pendingIdentity) {
            setSession({ ...session!, state: VIGState.CONFIRMED, vehicleData: { ...pendingIdentity, confidence: 100 } });
            handleSubmit();
        }
    };

    const calcConfidence = () => {
        if (!diagnosis) return 0;
        return confidenceEngine.calculateScore({
            vehicleData: { hasVin: !!vin, isComplete: isVehicleReady },
            fieldTests: { performed: diagnosis.technicalGuide?.technical_steps?.length || 0, isValid: true },
            historicalMatch: isDbResult,
            messageClarity: 0.9
        });
    };

    const confidencePercent = Math.round(calcConfidence());

    return (
        <div className="min-h-screen bg-[#121212] text-white font-sans antialiased overflow-x-hidden" dir="rtl">
            <header className="flex items-center justify-between p-4 border-b border-white/5 bg-[#1a1a1a]/80 backdrop-blur-md sticky top-0 z-50">
                <div className="flex items-center gap-3">
                    <div className="size-10 bg-primary/20 rounded-xl flex items-center justify-center border border-primary/20">
                        <span className="material-symbols-outlined text-primary font-black text-xl">build_circle</span>
                    </div>
                    <h1 className="text-lg font-black tracking-tight leading-none">الأسطى ناز <span className="text-[10px] bg-primary/10 px-1.5 py-0.5 rounded text-primary font-mono">v4.6</span></h1>
                </div>
                <div className="flex items-center gap-2">
                    <div className="bg-white/5 rounded-xl border border-white/10 p-1 flex">
                        <button onClick={() => setActiveTab('customer')} className={`px-4 py-2 rounded-lg text-xs font-bold ${activeTab === 'customer' ? 'bg-primary text-black' : 'text-gray-400'}`}>زبون</button>
                        <button onClick={() => isVerifiedTechnician ? setActiveTab('technician') : setShowTechAuth(true)} className={`px-4 py-2 rounded-lg text-xs font-bold ${activeTab === 'technician' ? 'bg-primary text-black' : 'text-gray-400'}`}>فني</button>
                        <button onClick={() => setActiveTab('maintenance')} className={`px-4 py-2 rounded-lg text-xs font-bold ${activeTab === 'maintenance' ? 'bg-primary text-black' : 'text-gray-400'}`}>صيانة</button>
                        {authSession?.role === 'admin' && <button onClick={() => setActiveTab('admin')} className={`px-4 py-2 rounded-lg text-xs font-bold ${activeTab === 'admin' ? 'bg-primary text-black' : 'text-gray-400'}`}>إشراف</button>}
                    </div>
                    <button onClick={handleSoftReset} className="size-9 rounded-lg bg-white/5 flex items-center justify-center text-gray-400"><span className="material-symbols-outlined text-lg">restart_alt</span></button>
                </div>
            </header>

            <main className="p-4 md:p-6 max-w-7xl mx-auto">
                {activeTab === 'admin' && <AdminDashboard />}
                {activeTab === 'maintenance' && (
                    <div className="bg-charcoal-light/50 rounded-3xl p-8 border border-white/5 text-center py-20 text-gray-500">
                        <span className="material-symbols-outlined text-6xl mb-4">history</span>
                        <p>لا توجد سجلات صيانة.</p>
                    </div>
                )}
                {(activeTab === 'customer' || activeTab === 'technician') && (
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start">
                        <div className="md:col-span-7 flex flex-col gap-6">
                            {!diagnosis && !loading && (
                                <div className="bg-charcoal-light/50 rounded-3xl p-8 border border-white/5 text-center py-20">
                                    <div className="bg-primary/10 size-20 rounded-full flex items-center justify-center mx-auto mb-6"><span className="material-symbols-outlined text-primary text-5xl">auto_fix</span></div>
                                    <h2 className="text-2xl font-bold mb-2">جاهز للتشخيص يا هندسة</h2>
                                    <p className="text-gray-400">أوصف لي العطل، أنا هنا عشان نحدد العطل بدقة.</p>
                                </div>
                            )}
                            {loading && <div className="bg-charcoal-light/50 rounded-3xl p-8 border border-white/5 text-center min-h-[400px] flex items-center justify-center"><p className="animate-pulse text-primary">جاري التحليل...</p></div>}
                            {diagnosis && !loading && (
                                <div className="bg-charcoal-light rounded-3xl overflow-hidden border border-white/10 shadow-2xl">
                                    <div className={`p-4 flex items-center justify-between ${diagnosis.risk_level === 'high' ? 'bg-red-500/10 text-red-500' : 'bg-green-500/10 text-green-500'}`}>
                                        <span className="font-bold text-xs">{diagnosis.risk_level === 'high' ? 'عطل حرج' : 'الحالة آمنة'}</span>
                                        <span className="text-[10px] font-bold opacity-70">{diagnosisSource}</span>
                                    </div>
                                    <div className="p-6 md:p-8">
                                        <h3 className="text-2xl font-bold text-white mb-4">{diagnosis.customerReport?.title}</h3>
                                        <p className="text-gray-300 text-sm leading-relaxed mb-6">{diagnosis.customerReport?.explanation}</p>
                                        {activeTab === 'technician' && isVerifiedTechnician && (
                                            <div className="space-y-6 pt-6 border-t border-white/5">
                                                <div className="grid grid-cols-2 gap-3">
                                                    <div className="bg-black/20 p-4 rounded-xl border border-white/5">
                                                        <span className="text-[10px] text-gray-500 block mb-1">الثقة</span>
                                                        <span className="text-xl font-bold text-primary">{confidencePercent}%</span>
                                                    </div>
                                                    <div className="bg-black/20 p-4 rounded-xl border border-white/5">
                                                        <span className="text-[10px] text-gray-500 block mb-1">المخطط الفني</span>
                                                        <button onClick={() => setShowDiagram(true)} className="text-sm font-bold text-blue-400 underline">{diagnosis.technicalGuide?.diagram_label || "N/A"}</button>
                                                    </div>
                                                </div>
                                                <PartsIntelligenceCard parts={parts} loading={loadingParts} onRequestRarePart={(p) => { setSelectedRarePart(p); setRarePartModalOpen(true); }} />
                                                <RequestsStatusBoard requests={myRareRequests} onRefresh={fetchMyRequests} onViewQuotes={() => { }} />
                                            </div>
                                        )}
                                    </div>
                                    <div className="p-4 bg-white/5 border-t border-white/10 flex gap-3">
                                        <button onClick={() => setShowSaveModal(true)} className="flex-1 bg-white/10 py-3 rounded-xl text-sm font-bold">حفظ التقرير</button>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="md:col-span-5 flex flex-col gap-6">
                            <div className="bg-charcoal-light/50 p-6 rounded-3xl border border-white/10 sticky top-24">
                                <div className="flex p-1 bg-white/5 rounded-xl border border-white/10 mb-4">
                                    <button onClick={() => setGatewayMode('manual')} className={`flex-1 py-2 text-xs font-bold rounded-lg ${gatewayMode === 'manual' ? 'bg-white/10 text-white' : 'text-gray-500'}`}>يدوي</button>
                                    <button onClick={() => setGatewayMode('vin')} className={`flex-1 py-2 text-xs font-bold rounded-lg ${gatewayMode === 'vin' ? 'bg-white/10 text-white' : 'text-gray-500'}`}>شاسي</button>
                                </div>
                                {gatewayMode === 'vin' ? (
                                    <input type="text" value={vin} onChange={(e) => setVin(e.target.value.toUpperCase())} maxLength={17} className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-4 text-center font-mono text-sm" placeholder="VIN CODE" />
                                ) : (
                                    <div className="grid grid-cols-2 gap-2">
                                        <select value={vehicleInfo.make} onChange={(e) => setVehicleInfo({ ...vehicleInfo, make: e.target.value })} className="bg-black/20 border border-white/10 rounded-xl p-3 text-xs text-white outline-none">
                                            <option value="">الماركة</option>
                                            {["تويوتا", "هيونداي", "كيا", "ميتسوبيشي", "نيسان"].map(m => <option key={m} value={m}>{m}</option>)}
                                        </select>
                                        <input type="text" value={vehicleInfo.model} onChange={(e) => setVehicleInfo({ ...vehicleInfo, model: e.target.value })} className="bg-black/20 border border-white/10 rounded-xl p-3 text-xs text-white" placeholder="الموديل" />
                                    </div>
                                )}
                                <div className="mt-6">
                                    <textarea value={userMessage} onChange={(e) => setUserMessage(e.target.value)} className="w-full bg-black/20 border border-white/10 rounded-2xl p-4 min-h-[120px] text-sm text-white" placeholder="اوصف المشكلة..." />
                                    <div className="flex items-center justify-between mt-3">
                                        <button onClick={isRecording ? stopListening : startListening} className={`size-10 rounded-xl flex items-center justify-center ${isRecording ? 'bg-red-500' : 'bg-white/5'}`}><span className="material-symbols-outlined text-white">{isRecording ? 'mic' : 'mic_none'}</span></button>
                                        <button onClick={() => handleSubmit()} disabled={loading || !isVehicleReady} className={`px-6 py-2.5 rounded-xl font-bold text-sm ${loading || !isVehicleReady ? 'bg-white/5 text-gray-500' : 'bg-primary text-black'}`}>تشخيص</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </main>

            {/* Modals */}
            {showTechAuth && (
                <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-xl flex items-center justify-center p-4">
                    <div className="bg-charcoal-dark border border-white/10 rounded-3xl p-8 w-full max-w-sm">
                        <h3 className="text-xl font-bold text-white mb-6 text-center">بوابة الفنيين</h3>
                        <input type="password" value={techCodeInput} onChange={(e) => setTechCodeInput(e.target.value)} className="w-full bg-black/40 border border-white/10 rounded-xl p-4 text-center text-xl mb-6 outline-none" placeholder="الكود" />
                        <div className="flex gap-3"><button onClick={() => setShowTechAuth(false)} className="flex-1 py-3 bg-white/5 rounded-xl text-sm font-bold text-gray-400">إلغاء</button><button onClick={handleTechAuthSubmit} className="flex-1 py-3 bg-primary text-black rounded-xl text-sm font-bold">دخول</button></div>
                    </div>
                </div>
            )}

            {showSaveModal && (
                <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-xl flex items-center justify-center p-4">
                    <div className="bg-charcoal-dark border border-white/10 rounded-3xl p-8 w-full max-w-sm text-center">
                        <h3 className="text-xl font-bold text-white mb-2">حفظ التقرير</h3>
                        <p className="text-xs text-gray-400 mb-6 underline decoration-primary/50 underline-offset-4">سيتم إرسال نسخة PDF لهاتفك</p>
                        <div className="space-y-3">
                            <input type="text" placeholder="الاسم" className="w-full bg-black/40 border border-white/10 rounded-xl p-3 text-right text-white" />
                            <input type="text" placeholder="رقم الهاتف" className="w-full bg-black/40 border border-white/10 rounded-xl p-3 text-right text-white" />
                        </div>
                        <div className="flex gap-3 mt-6"><button onClick={() => setShowSaveModal(false)} className="flex-1 py-3 bg-white/5 rounded-xl text-sm font-bold text-gray-400">إلغاء</button><button onClick={() => { alert("تم الحفظ!"); setShowSaveModal(false); }} className="flex-1 py-3 bg-green-600 rounded-xl text-sm font-bold">حفظ</button></div>
                    </div>
                </div>
            )}

            {diagnosis && <DiagramViewer isOpen={showDiagram} onClose={() => setShowDiagram(false)} diagramLabel={diagnosis.technicalGuide?.diagram_label || ""} explanation={diagnosis.technicalGuide?.diagram_explanation || ""} hotspots={diagnosis.hotspots as any || []} />}
            <RarePartsRequestModal isOpen={rarePartModalOpen} onClose={() => setRarePartModalOpen(false)} onSubmit={handleRarePartSubmit} vehicleInfo={{ make: vehicleInfo.make, model: vehicleInfo.model, year: vehicleInfo.year, vin }} />
            {pendingIdentity && <VIGConfirmationModal isOpen={showVIGConfirm} identity={pendingIdentity} onConfirm={handleVIGConfirmed} onCancel={() => setShowVIGConfirm(false)} />}
        </div>
    );
}

export default App;
